/* eslint-disable no-empty */
import { ColDef, ColumnState } from '@ag-grid-community/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import {
  EntityListField,
  getGridColumnType,
  SharedFacadeInterface,
  tabDownloadExcel,
} from '@ellf/app-common';
import { RouterService } from '@ellf/app-routing';
import { DataGridAction } from '@ellf/app-ui-kit';
import { BaseDbDto } from '@ellf/common-dto';
import { FilterValue } from '@ellf/common-interfaces';

@Component({
    template: '',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class BaseEntityDataGridComponent<T extends BaseDbDto>
  implements OnInit
{
  @Input()
  items: Partial<T>[] = [];

  @Input()
  canEdit = false;
  @Input()
  canDelete = false;
  @Input()
  canView = false;
  @Input()
  canCreate = false;
  @Input()
  canDownload = false;

  @Input()
  customActions: DataGridAction[] = [];

  @Input()
  parentConfig?: {
    id: string;
    addQueryParams: { key: string; value: string }[];
    download: {
      fileName: string;
      filters: FilterValue[];
      join?: string[];
    };
  };

  @Input()
  canSelect = false;

  private _excludeColumns: string[] = [];
  get excludeColumns() {
    return this._excludeColumns;
  }

  @Input()
  set excludeColumns(value: string[]) {
    this._excludeColumns = value;
    this.calcColumns();
  }

  @Output()
  added = new EventEmitter<T>();
  @Output()
  edited = new EventEmitter<T>();
  @Output()
  deleted = new EventEmitter<T>();

  protected abstract visibleColumns: string[];
  protected abstract entityName: string;
  protected abstract gridColumns: EntityListField[];

  protected columnState: ColumnState[] = [];
  public columns: ColDef[] = [];

  constructor(
    @Inject('SharedEntityFacade')
    protected sharedFacade: SharedFacadeInterface<T>,
    protected routerService: RouterService
  ) {}

  ngOnInit(): void {
    this.calcColumns();
  }

  calcColumns(onlyVisible = false) {
    if (onlyVisible) {
      this.columns = this.visibleColumns.map((c) => {
        const f = this.gridColumns.find(
          (gc) => gc.field === c
        ) as EntityListField;
        return {
          colId: f.field,
          initialHide: false,
          field: f.field,
          headerName: f.elementLabel,
          type: getGridColumnType(f.filterType, f.fieldType),
          sortable: f.sortable,
          cellRendererParams: {
            elementValues: f.elementValues,
          },
          sort: f.sort,
          autoHeight: true,
          cellStyle: { 'white-space': 'normal' },
        } as ColDef;
      });
    } else {
      this.columns = this.gridColumns
        .filter((gc) => !this.excludeColumns.find((c) => c === gc.field))
        .map(
          (f) =>
            ({
              colId: f.field,
              initialHide: !this.visibleColumns.find((c) => c === f.field),
              field: f.field,
              headerName: f.elementLabel,
              type: getGridColumnType(f.filterType, f.fieldType),
              sortable: f.sortable,
              cellRendererParams: {
                elementValues: f.elementValues,
              },
              sort: f.sort,
              autoHeight: true,
              cellStyle: { 'white-space': 'normal' },
            } as ColDef)
        );
    }
  }

  async edit(item: T) {
    try {
      await this.routerService.editEntityDetail(this.entityName, item.id);

      this.edited.emit(item);
    } catch (error) {}
  }

  async view(item: T) {
    try {
      await this.routerService.viewEntityDetail(this.entityName, item.id);
    } catch (error) {}
  }

  delete(item: T) {
    if (confirm('Confermi di voler eliminare questo elemento?')) {
      this.sharedFacade.delete(item.id, true, false, async () => {
        this.deleted.emit(item);
      });
    }
  }
  async add() {
    if (this.parentConfig) {
      try {
        const query = this.parentConfig.addQueryParams
          .map((x) => {
            return x.key + '=' + x.value;
          })
          .join('&');
        const item = await this.routerService.addNewEntity(
          this.entityName,
          query
        );
        this.added.emit(item);
      } catch (error) {}
    }
  }

  download() {
    if (this.parentConfig) {
      let join: string[] = [];
      if (
        this.parentConfig.download.join &&
        this.parentConfig.download.join.length
      ) {
        // get join from select columns otherwise excel doesn't contain all informations
        const relations = this.columnState
          .filter((c) => !c.hide)
          .filter((c) => c.colId.indexOf('.') > 0);
        join = [...(this.parentConfig.download.join || [])];
        relations.forEach((r) => {
          const relation = r.colId.substring(0, r.colId.lastIndexOf('.'));
          if (!join.includes(relation)) {
            join.push(relation);
          }
        });
      }

      tabDownloadExcel(
        this.sharedFacade,
        this.gridColumns,
        this.excludeColumns,
        this.columnState,
        this.parentConfig.download.fileName,
        this.parentConfig.download.filters,
        join.length ? join : undefined
      );
    }
  }

  columnStateChanged(event: ColumnState[]) {
    this.columnState = event;
  }
}
